import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CategoryStep, PhotosStep, DetailsStep, ContactStep } from "@/pages/PlaceAdSteps";

const STEPS = ["Category", "Photos", "Details", "Contact"];

export default function PlaceAd() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState({});
  const [counties, setCounties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fees, setFees] = useState({ listing_fee: 5, spotlight_fee: 10 });
  const [editStatus, setEditStatus] = useState(null);

  const [form, setForm] = useState({
    category: "", subcategory: "", images: [], title: "", price: "", description: "",
    county: "", area: "", eircode: "", condition: "Used", contact_option: "show_phone", phone: "", premium: false, specs: {},
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...(f.specs || {}), [k]: v } }));

  useEffect(() => {
    api.get("/categories").then((r) => { setCats(r.data.categories); setCounties(r.data.counties); });
    api.get("/payments/config").then((r) => setFees(r.data)).catch((e) => console.error("Failed to load listing fees", e));
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/listings/${editId}`).then((r) => {
      const l = r.data;
      setEditStatus(l.status);
      setForm({
        category: l.category || "", subcategory: l.subcategory || "", images: l.images || [],
        title: l.title || "", price: l.price ?? "", description: l.description || "",
        county: l.county || "", area: l.area || "", eircode: l.eircode || "", condition: l.condition || "Used",
        contact_option: l.contact_option || "show_phone", phone: l.phone || "", premium: !!l.premium,
        specs: l.specs || {},
      });
    }).catch(() => toast.error("Could not load that ad"));
  }, [editId]);

  const isEditing = !!editId;
  const isAdmin = !!user && user.role === "admin";
  const freeAvailable = isAdmin || (!!user && !user.free_ad_used);
  const effListingFee = freeAvailable ? 0 : fees.listing_fee;
  // Admins post at no cost (listing fee + Spotlight both waived).
  const total = isAdmin ? 0 : effListingFee + (form.premium ? fees.spotlight_fee : 0);

  const stepIndicatorClass = (i) => {
    if (i < step) return "bg-emerald-600 text-white";
    if (i === step) return "bg-emerald-600 text-white ring-4 ring-emerald-100";
    return "bg-slate-200 text-slate-500";
  };
  let publishLabel;
  if (busy) publishLabel = "Publishing...";
  else if (total === 0) publishLabel = "Publish for Free";
  else publishLabel = `Pay €${total.toFixed(2)} & Publish`;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files).slice(0, 6 - form.images.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const results = await Promise.allSettled(files.map((file) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    }));
    const urls = [];
    let failed = 0;
    results.forEach((r) => { if (r.status === "fulfilled") urls.push(r.value.data.url); else failed += 1; });
    if (urls.length) setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
    if (failed) toast.error(`${failed} image${failed > 1 ? "s" : ""} couldn't be uploaded, please try again`);
    setUploading(false);
  };
  const removeImg = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const canNext = () => {
    if (step === 0) return form.category && form.subcategory;
    if (step === 2) return form.title && form.price !== "" && form.description && form.county;
    return true;
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, status: editStatus || "active" };
      await api.put(`/listings/${editId}`, payload);
      toast.success("Changes saved");
      navigate(editStatus === "active" ? `/listing/${editId}` : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const submit = async (status) => {
    setBusy(true);
    try {
      if (status === "draft") {
        const payload = { ...form, price: Number(form.price) || 0, status: "draft" };
        await api.post("/listings", payload);
        toast.success("Saved as draft");
        navigate("/dashboard");
        return;
      }
      // Publish: create as pending, then either activate free (first ad) or go to Stripe.
      const payload = { ...form, price: Number(form.price) || 0, status: "pending_payment" };
      const { data } = await api.post("/listings", payload);
      const { data: checkout } = await api.post("/payments/checkout-listing", {
        listing_id: data.id, origin_url: window.location.origin,
      });
      if (checkout.url) {
        window.location.href = checkout.url;
        return;
      }
      // Activated without a Stripe redirect: either the free first ad or paid via wallet credits.
      await refreshUser();
      let msg;
      if (checkout.admin) msg = "Ad published for free (admin), it's live!";
      else if (checkout.paid_with_credits) msg = `Paid €${checkout.amount.toFixed(2)} with your DealShed credits, your ad is live!`;
      else msg = "Your first ad is live, on the house!";
      toast.success(msg);
      navigate(`/listing/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8 w-full flex-1">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-6">{isEditing ? "Edit your ad" : "Place an Ad"}</h1>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-2`}>
                <span className={`grid place-items-center w-8 h-8 rounded-full text-sm font-semibold ${stepIndicatorClass(i)}`}>
                  {i < step ? <Check size={16} /> : i + 1}
                </span>
                <span className={`text-sm hidden sm:inline ${i === step ? "font-semibold text-slate-900" : "text-slate-500"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-emerald-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          {step === 0 && <CategoryStep cats={cats} form={form} set={set} />}
          {step === 1 && <PhotosStep form={form} uploading={uploading} onFiles={onFiles} removeImg={removeImg} />}
          {step === 2 && <DetailsStep form={form} set={set} setSpec={setSpec} counties={counties} />}
          {step === 3 && <ContactStep form={form} set={set} fees={fees} freeAvailable={freeAvailable} total={total} isEditing={isEditing} />}

          {/* Nav */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
            <Button variant="ghost" data-testid="step-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)}><ArrowLeft size={16} className="mr-1" /> Back</Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="step-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Next <ArrowRight size={16} className="ml-1" /></Button>
            ) : isEditing ? (
              <Button data-testid="save-changes" disabled={busy} onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Saving..." : "Save changes"}</Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" data-testid="save-draft" disabled={busy} onClick={() => submit("draft")}>Save draft</Button>
                <Button data-testid="publish-ad" disabled={busy} onClick={() => submit("active")} className="bg-emerald-600 hover:bg-emerald-700 text-white">{publishLabel}</Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
